// Lib
import { FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// Api
import { useGetCustomerMenuByLocationQuery } from "rtkQuery/query/customersAPI";
import { useGetLocationsQuery } from "rtkQuery/query/locationsAPI";
// Hooks
import { useNotification, useTable, useViewport } from "hooks";
// Constants
import { ADMIN_ROUTES } from "consts";
// Types
import { TableAction } from "types/common";
import { ETable } from "types/tableFilters";
import { CustomerMenuProduct } from "types/customers";
// Helpers
import { isMatch } from "helpers/dataHelpers";
import { transformToTableData } from "./helpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { PencilIcon } from "icons";
// Components
import { Pagination, Table } from "components";
import { Select } from "components/Form";
// Styled
import { FlexContainer, PageWrapper } from "styled/Box";
import { Typography } from "styled/Typography";

import { columns } from "./config";

export const ProductMedia: FC = () => {
  const { openNotification } = useNotification();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { isMobile, isDesktop } = useViewport();

  const {
    sortingOrder,
    sortBy,
    page,
    limit,
    search,
    setPage,
    setLimit,
    setSearch,
    handleSort,
  } = useTable({
    name: ETable.ProductMedia,
    removeQueryParams: true,
  });

  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(
    () => searchParams.get("locationId") || undefined,
  );

  const [sortedData, setSortedData] = useState<CustomerMenuProduct[]>([]);

  const {
    data: locations,
    isLoading: isLocationsLoading,
    error: locationsError,
  } = useGetLocationsQuery(null);

  const {
    data: menu,
    isLoading: isMenuLoading,
    isFetching: isMenuFetching,
    error: menuError,
  } = useGetCustomerMenuByLocationQuery(
    {
      id: selectedLocation || "",
    },
    { skip: !selectedLocation },
  );

  useEffect(() => {
    if (locationsError) {
      errorHandler({ error: locationsError, openNotification });
    }
  }, [locationsError]);

  useEffect(() => {
    if (menuError) {
      errorHandler({ error: menuError, openNotification });
    }
  }, [menuError]);

  useEffect(() => {
    if (!menu || !selectedLocation) {
      setSortedData([]);
      return;
    }

    if (
      !sortingOrder &&
      !search &&
      !isMatch(transformToTableData(menu), sortedData)
    ) {
      setSortedData(transformToTableData(menu));
      return;
    }
    const sortedArray = [...transformToTableData(menu)]
      .sort((a, b) => {
        const valA = a[sortBy];
        const valB = b[sortBy];
        if (valA < valB) return sortingOrder === "ASC" ? -1 : 1;
        if (valA > valB) return sortingOrder === "ASC" ? 1 : -1;
        return 0;
      })
      .filter(el =>
        search ? el.name.toLowerCase().includes(search.toLowerCase()) : el,
      );
    setSortedData(sortedArray);
  }, [menu, search, sortBy, sortingOrder, selectedLocation]);

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter: any,
  ) => {
    handleSort(sorter?.field, sorter?.order);
  };

  const paginate = (
    array: CustomerMenuProduct[],
    currentPage: number,
    pageSize: number,
  ) => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return array?.slice(start, end);
  };

  const paginatedData = paginate(sortedData || [], page, limit);

  const locationsOptions =
    locations?.map(loc => ({ value: loc.id, label: loc?.name })) || [];

  const actions: TableAction[] = [
    {
      title: "",
      Icon: PencilIcon,
      type: "Grey",
      onClick: (record: CustomerMenuProduct) =>
        navigate(
          `${ADMIN_ROUTES.PRODUCT_MEDIA.path}/edit/${record.providerId}/${selectedLocation}/${record.id}`,
        ),
    },
  ];

  return (
    <PageWrapper $fullwidth $column>
      <FlexContainer
        $fullwidth
        $column
        $grow={1}
        $padding={isDesktop && "0 0 32px"}
      >
        <FlexContainer
          $column={isMobile}
          $padding="0 0 24px"
          $align={isMobile ? "flex-start" : "center"}
          $justify="space-between"
          $gap={16}
        >
          <Typography.H1>Product Media</Typography.H1>

          <Select
            style={{ maxWidth: 200, minWidth: 200 }}
            placeholder="Location"
            value={selectedLocation}
            options={locationsOptions}
            onChange={(value: string) => setSelectedLocation(value)}
          />
        </FlexContainer>

        <Table
          header={{
            totalCount: sortedData?.length,
            search: { setValue: setSearch, value: search },
          }}
          columns={columns}
          actions={actions}
          isLoading={isLocationsLoading || isMenuLoading || isMenuFetching}
          dataSource={paginatedData}
          onChange={handleTableChange}
        />
      </FlexContainer>

      <Pagination
        onPage
        padding="12px 16px"
        limit={limit}
        page={page}
        setLimit={setLimit}
        setPage={setPage}
        showSizeChanger={!isDesktop}
        totalItems={sortedData?.length}
      />
    </PageWrapper>
  );
};
